import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { categoriesAPI, productGroupsAPI } from '../../services/api';
import { subcategoriesAPI } from '../../services/subcategoriesAPI';
import { ArrowLeft, Save, X } from 'lucide-react';
import toast from 'react-hot-toast';
import ImageUpload from '../../components/ImageUpload';

const SubcategoryForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedImage, setSelectedImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedCategoryUuid, setSelectedCategoryUuid] = useState('');
  const isEditing = Boolean(id);

  // Fetch subcategory data if editing
  const { data: subcategoryData, isLoading: isLoadingSubcategory } = useQuery(
    ['subcategory', id],
    () => subcategoriesAPI.getById(id),
    {
      enabled: isEditing,
      onError: (error) => {
        console.error('Failed to fetch subcategory:', error);
        toast.error('Failed to load subcategory data');
        navigate('/subcategories');
      }
    }
  );

  const subcategory = subcategoryData?.data?.data || subcategoryData?.data;

  // Fetch categories
  const { data: categoriesData } = useQuery('categories', () => {
    return categoriesAPI.getAll().then(response => response);
  }, {
    retry: 1,
    onError: (error) => {
      console.error('Categories fetch error:', error);
    }
  });

  // Fetch product groups for selected category
  const { data: productGroupsData } = useQuery(
    ['product-groups', selectedCategoryUuid], 
    () => selectedCategoryUuid ? productGroupsAPI.getAll({ category_uuid: selectedCategoryUuid }) : Promise.resolve({ data: [] }),
    {
      enabled: !!selectedCategoryUuid,
      retry: 1,
      onError: (error) => {
        console.error('Product groups fetch error:', error);
      }
    }
  );

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      description: '',
      product_group_uuid: '',
      sort_order: '',
    }
  });

  // Set form values when subcategory data is loaded
  useEffect(() => {
    if (subcategory) {
      setValue('name', subcategory.name || '');
      setValue('description', subcategory.description || '');
      setValue('product_group_uuid', subcategory.product_group_uuid || '');
      setValue('sort_order', subcategory.sort_order || '');
      
      // Also set the category for the product group selector
      if (subcategory.product_group_uuid) {
        // Find which category this product group belongs to
        const allProductGroups = productGroupsData?.data?.data || productGroupsData?.data || [];
        const productGroup = allProductGroups.find(pg => pg.uuid === subcategory.product_group_uuid);
        if (productGroup) {
          setSelectedCategoryUuid(productGroup.category_uuid);
        }
      }
    }
  }, [subcategory, setValue, productGroupsData]);

  // Process data
  const categories = Array.isArray(categoriesData?.data?.data) ? categoriesData.data.data : 
                    Array.isArray(categoriesData?.data) ? categoriesData.data : [];
                    
  const productGroups = Array.isArray(productGroupsData?.data?.data) ? productGroupsData.data.data : 
                       Array.isArray(productGroupsData?.data) ? productGroupsData.data : [];

  const mutation = useMutation(
    async (formData) => {
      if (isEditing) {
        return subcategoriesAPI.update(id, formData);
      } else {
        return subcategoriesAPI.create(formData);
      }
    },
    {
      onSuccess: () => {
        toast.success(isEditing ? 'Subcategory updated successfully!' : 'Subcategory created successfully!');
        queryClient.invalidateQueries('subcategories');
        navigate('/subcategories');
      },
      onError: (error) => {
        console.error('Mutation error:', error);
        toast.error(error.response?.data?.message || 'Operation failed');
      }
    }
  );

  const onSubmit = async (data) => {
    setIsUploading(true);
    try {
      // Validate image is selected for new subcategories
      if (!selectedImage && !subcategory?.image_url && !isEditing) {
        toast.error('Please select an image');
        setIsUploading(false);
        return;
      }

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('description', data.description || '');
      formData.append('product_group_uuid', data.product_group_uuid);
      formData.append('sort_order', data.sort_order || '0');
      
      // Add image if selected
      if (selectedImage) {
        formData.append('image', selectedImage);
      }
      
      mutation.mutate(formData);
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to save subcategory');
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageChange = (file) => {
    setSelectedImage(file);
  };

  const handleCancel = () => {
    navigate('/subcategories');
  };

  if (isLoadingSubcategory) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={handleCancel}
              className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {isEditing ? `Edit Subcategory: ${subcategory?.name}` : 'Create New Subcategory'}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {isEditing ? 'Update subcategory information' : 'Add a new subcategory to your inventory'}
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
            {/* Subcategory Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Subcategory Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('name', { required: 'Subcategory name is required' })}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter subcategory name"
              />
              {errors.name && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.name.message}</p>
              )}
            </div>

            {/* Hierarchical Selection: Category → Product Group */}
            <div className="space-y-6">
              {/* Category Selection (Step 1) */}
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border-l-4 border-primary-500">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
                  📁 Step 1: Select Category
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(Choose category first)</span>
                </h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedCategoryUuid}
                    onChange={(e) => {
                      setSelectedCategoryUuid(e.target.value);
                      setValue('product_group_uuid', ''); // Clear product group when category changes
                    }}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="">Select a category</option>
                    {categories.map((category) => (
                      <option key={category.uuid} value={category.uuid}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Product Group Selection (Step 2) */}
              {selectedCategoryUuid && (
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border-l-4 border-blue-500">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center">
                    📦 Step 2: Select Product Group
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                      (From category: {categories.find(c => c.uuid === selectedCategoryUuid)?.name})
                    </span>
                  </h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Product Group <span className="text-red-500">*</span>
                    </label>
                    <select
                      {...register('product_group_uuid', { required: 'Product Group is required' })}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    >
                      <option value="">Select a product group</option>
                      {productGroups.map((productGroup) => (
                        <option key={productGroup.uuid} value={productGroup.uuid}>
                          {productGroup.name}
                        </option>
                      ))}
                    </select>
                    {errors.product_group_uuid && (
                      <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.product_group_uuid.message}</p>
                    )}
                    {productGroups.length === 0 && (
                      <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                        No product groups available for this category
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {!selectedCategoryUuid && (
                <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Select a category above to see available product groups
                  </p>
                </div>
              )}
            </div>

            {/* Sort Order */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Sort Order
              </label>
              <input
                type="number"
                {...register('sort_order', { 
                  valueAsNumber: true,
                  min: { value: 0, message: 'Sort order must be 0 or greater' }
                })}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter sort order (e.g., 1, 2, 3...)"
                min="0"
              />
              {errors.sort_order && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.sort_order.message}</p>
              )}
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Lower numbers will appear first. Leave empty to auto-assign.
              </p>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                {...register('description')}
                rows={4}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter subcategory description"
              />
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Subcategory Image {!isEditing && <span className="text-red-500">*</span>}
              </label>
              <ImageUpload
                value={subcategory?.image_url}
                onChange={handleImageChange}
                label="Subcategory Image"
                required={!isEditing}
                error={!selectedImage && !subcategory?.image_url && !isEditing ? 'Image is required' : ''}
              />
            </div>

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={handleCancel}
                className="flex-1 sm:flex-none px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 flex items-center justify-center gap-2"
              >
                <X className="h-4 w-4" />
                Cancel
              </button>
              <button
                type="submit"
                disabled={isUploading || mutation.isLoading}
                className="flex-1 sm:flex-none px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center gap-2"
              >
                {isUploading || mutation.isLoading ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {isEditing ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    {isEditing ? 'Update Subcategory' : 'Create Subcategory'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SubcategoryForm;